import { AppLayout } from "@/components/layout/AppLayout";
import { DashboardStats } from "@/components/dashboard/DashboardStats";
import { RecentOrders } from "@/components/dashboard/RecentOrders";
import { PendingActions } from "@/components/dashboard/PendingActions";
import { VendorAnalytics } from "@/components/dashboard/VendorAnalytics";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Activity, TrendingUp, Plus, Package, Users, ShoppingCart, FileText, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProcurement } from "@/context/ProcurementContext";
import { useState, useEffect } from "react";
import { toast } from "sonner";

const Index = () => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Safe context access with error handling
  let actions;
  try {
    const context = useProcurement();
    actions = context.actions;
  } catch (error) {
    console.error('Context error in Index:', error);
    // Fallback actions
    actions = {
      fetchSKUs: async () => {},
      fetchVendors: async () => {},
      fetchPurchaseOrders: async () => {},
      fetchQualityInspections: async () => {},
      fetchInvoices: async () => {}
    };
  }

  // Load all data when dashboard mounts
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        await Promise.all([
          actions.fetchSKUs(),
          actions.fetchVendors(),
          actions.fetchPurchaseOrders(),
          actions.fetchQualityInspections(),
          actions.fetchInvoices()
        ]);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      }
    };

    loadDashboardData();
  }, []); // Empty dependency array - only run once on mount

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // First refresh the mock data to ensure we have the latest from mockData.ts
      if (actions.refreshMockData) {
        await actions.refreshMockData();
      }
      
      await Promise.all([
        actions.fetchSKUs(),
        actions.fetchVendors(),
        actions.fetchPurchaseOrders(),
        actions.fetchQualityInspections(),
        actions.fetchInvoices()
      ]);
      toast.success("Dashboard data refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh dashboard data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const quickActions = [
    {
      title: "Create SKU",
      description: "Add new inventory item",
      icon: Package,
      action: () => navigate("/skus"),
      color: "bg-blue-500 hover:bg-blue-600"
    },
    {
      title: "Add Vendor",
      description: "Register new supplier",
      icon: Users,
      action: () => navigate("/vendors"),
      color: "bg-green-500 hover:bg-green-600"
    },
    {
      title: "Create PO",
      description: "New purchase order",
      icon: ShoppingCart,
      action: () => navigate("/purchase-orders"),
      color: "bg-purple-500 hover:bg-purple-600"
    },
    {
      title: "New Invoice",
      description: "Create invoice",
      icon: FileText,
      action: () => navigate("/invoices"),
      color: "bg-orange-500 hover:bg-orange-600"
    }
  ];

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Welcome to KKG Electricals Management System</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        <DashboardStats onNavigate={navigate} />

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common tasks and shortcuts for faster workflow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-auto p-4 flex flex-col items-center gap-2 hover:shadow-md transition-all"
                    onClick={action.action}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white ${action.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-sm">{action.title}</div>
                      <div className="text-xs text-muted-foreground">{action.description}</div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3">
            <TabsTrigger value="overview" className="flex items-center gap-2 text-xs sm:text-sm">
              <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Overview</span>
              <span className="xs:hidden">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="vendor-analytics" className="flex items-center gap-2 text-xs sm:text-sm">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Vendor Analytics</span>
              <span className="xs:hidden">Vendors</span>
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2 text-xs sm:text-sm">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Performance</span>
              <span className="xs:hidden">Performance</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <RecentOrders onNavigate={navigate} />
              <PendingActions onNavigate={navigate} />
            </div>
          </TabsContent>

          <TabsContent value="vendor-analytics" className="space-y-4">
            <VendorAnalytics onNavigate={navigate} />
          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <RecentOrders onNavigate={navigate} />
              <PendingActions onNavigate={navigate} />
            </div>
            <div className="mt-6">
              <VendorAnalytics onNavigate={navigate} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Index;
